import { useState, useMemo, useCallback } from 'react';
import {
  Box,
  Paper,
  IconButton,
  Modal,
  Button,
  Fade,
  Backdrop,
  styled,
} from '@mui/material';
import {
  ChevronLeft,
  ChevronRight,
  CalendarMonth,
} from '@mui/icons-material';

// ===== TYPES =====

export type SelectionMode = 'year' | 'year-month' | 'year-month-day';

export interface DateValue {
  year: number;
  month?: number; // 1-12
  day?: number;
}

export interface DatePickerProps {
  /** Selection mode: year only, year+month, or full date */
  mode: SelectionMode;
  /** Current value */
  value: DateValue;
  /** Change callback */
  onChange: (value: DateValue) => void;
  /** Minimum year */
  minYear?: number;
  /** Maximum year */
  maxYear?: number;
  /** Disabled state */
  disabled?: boolean;
  /** Size variant */
  size?: 'small' | 'medium';
  /** Show "Today" button */
  showTodayButton?: boolean;
  /** Custom placeholder */
  placeholder?: string;
  /** Function to check if a specific month should be disabled */
  isMonthDisabled?: (year: number, month: number) => boolean;
}

// ===== CONSTANTS =====

const MONTHS: Record<number, string> = {
  1: 'Январь',
  2: 'Февраль',
  3: 'Март',
  4: 'Апрель',
  5: 'Май',
  6: 'Июнь',
  7: 'Июль',
  8: 'Август',
  9: 'Сентябрь',
  10: 'Октябрь',
  11: 'Ноябрь',
  12: 'Декабрь',
};

const MONTHS_SHORT: Record<number, string> = {
  1: 'Янв',
  2: 'Фев',
  3: 'Мар',
  4: 'Апр',
  5: 'Май',
  6: 'Июн',
  7: 'Июл',
  8: 'Авг',
  9: 'Сен',
  10: 'Окт',
  11: 'Ноя',
  12: 'Дек',
};

const WEEKDAYS = ['Пн', 'Вт', 'Ср', 'Чт', 'Пт', 'Сб', 'Вс'];

// ===== STYLED COMPONENTS =====

const TriggerButton = styled(Button)(({ theme }) => ({
  textTransform: 'none',
  justifyContent: 'flex-start',
  padding: theme.spacing(1, 1.5),
  borderColor: theme.palette.divider,
  color: theme.palette.text.primary,
  '&:hover': {
    borderColor: theme.palette.text.primary,
    backgroundColor: 'transparent',
  },
}));

const CalendarHeader = styled(Box)(({ theme }) => ({
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'space-between',
  padding: theme.spacing(1),
  borderBottom: `1px solid ${theme.palette.divider}`,
}));

const HeaderTitle = styled(Button)(({ theme }) => ({
  textTransform: 'none',
  fontWeight: 600,
  fontSize: '0.95rem',
  color: theme.palette.text.primary,
  '&:hover': {
    backgroundColor: theme.palette.action.hover,
  },
}));

const GridCell = styled(Button, {
  shouldForwardProp: (prop) => !['isSelected', 'isToday', 'isCurrentMonth'].includes(prop as string),
})<{ isSelected?: boolean; isToday?: boolean; isCurrentMonth?: boolean }>(
  ({ theme, isSelected, isToday, isCurrentMonth = true }) => ({
    minWidth: 36,
    height: 36,
    padding: 0,
    borderRadius: theme.shape.borderRadius,
    fontWeight: isSelected || isToday ? 600 : 400,
    color: isSelected
      ? theme.palette.common.white
      : !isCurrentMonth
        ? theme.palette.text.disabled
        : isToday
          ? theme.palette.primary.main
          : theme.palette.text.primary,
    backgroundColor: isSelected
      ? theme.palette.primary.main
      : isToday
        ? theme.palette.primary.light + '20'
        : 'transparent',
    '&:hover': {
      backgroundColor: isSelected
        ? theme.palette.primary.dark
        : theme.palette.action.hover,
    },
  })
);

const MonthCell = styled(Button, {
  shouldForwardProp: (prop) => !['isSelected', 'isCurrent'].includes(prop as string),
})<{ isSelected?: boolean; isCurrent?: boolean }>(({ theme, isSelected, isCurrent }) => ({
  flex: '1 0 30%',
  margin: theme.spacing(0.5),
  padding: theme.spacing(1.5, 1),
  borderRadius: theme.shape.borderRadius,
  textTransform: 'none',
  fontWeight: isSelected || isCurrent ? 600 : 400,
  color: isSelected
    ? theme.palette.common.white
    : isCurrent
      ? theme.palette.primary.main
      : theme.palette.text.primary,
  backgroundColor: isSelected
    ? theme.palette.primary.main
    : isCurrent
      ? theme.palette.primary.light + '20'
      : theme.palette.action.hover,
  '&:hover': {
    backgroundColor: isSelected ? theme.palette.primary.dark : theme.palette.action.selected,
  },
}));

const YearCell = styled(MonthCell)({});

// ===== HELPER FUNCTIONS =====

function getDaysInMonth(year: number, month: number): number {
  return new Date(year, month, 0).getDate();
}

function getFirstDayOfMonth(year: number, month: number): number {
  // Returns 0 (Sun) to 6 (Sat), we need 0 (Mon) to 6 (Sun)
  const day = new Date(year, month - 1, 1).getDay();
  return day === 0 ? 6 : day - 1;
}

function formatDisplayValue(value: DateValue, mode: SelectionMode): string {
  const { year, month, day } = value;

  switch (mode) {
    case 'year':
      return `${year}`;
    case 'year-month':
      return month ? `${MONTHS[month]} ${year}` : `${year}`;
    case 'year-month-day':
      if (day && month) {
        return `${day.toString().padStart(2, '0')}.${month.toString().padStart(2, '0')}.${year}`;
      }
      if (month) {
        return `${MONTHS[month]} ${year}`;
      }
      return `${year}`;
    default:
      return `${year}`;
  }
}

// ===== COMPONENT =====

type ViewType = 'years' | 'months' | 'days';

export function DatePicker({
  mode,
  value,
  onChange,
  minYear = new Date().getFullYear() - 10,
  maxYear = new Date().getFullYear() + 10,
  disabled = false,
  size = 'small',
  showTodayButton = true,
  placeholder = 'Выберите дату',
  isMonthDisabled,
}: DatePickerProps) {
  const [open, setOpen] = useState(false);
  const [viewYear, setViewYear] = useState(value.year);
  const [viewMonth, setViewMonth] = useState(value.month || new Date().getMonth() + 1);
  const [currentView, setCurrentView] = useState<ViewType>('days');
  const [yearGroupStart, setYearGroupStart] = useState(() => {
    return Math.floor(value.year / 12) * 12;
  });

  // Get initial view for mode - this is where selection STARTS (like original CoreUICalendar)
  const getInitialViewForMode = useCallback((m: SelectionMode): ViewType => {
    switch (m) {
      case 'year':
        return 'years';
      case 'year-month':
        return 'months';
      case 'year-month-day':
        return 'days';
      default:
        return 'days';
    }
  }, []);

  const handleOpen = () => {
    if (disabled) return;
    setOpen(true);
    // Reset view based on mode when opening - start from target selection level
    setCurrentView(getInitialViewForMode(mode));
    setViewYear(value.year);
    setViewMonth(value.month || new Date().getMonth() + 1);
    setYearGroupStart(Math.floor(value.year / 12) * 12);
  };

  const handleClose = () => {
    setOpen(false);
  };

  const displayValue = useMemo(() => {
    if (!value.year) return placeholder;
    return formatDisplayValue(value, mode);
  }, [value, mode, placeholder]);

  // Navigation handlers
  const navigatePrevious = useCallback(() => {
    if (currentView === 'years') {
      setYearGroupStart((prev) => prev - 12);
    } else if (currentView === 'months') {
      setViewYear((prev) => prev - 1);
    } else {
      if (viewMonth === 1) {
        setViewMonth(12);
        setViewYear((prev) => prev - 1);
      } else {
        setViewMonth((prev) => prev - 1);
      }
    }
  }, [currentView, viewMonth]);

  const navigateNext = useCallback(() => {
    if (currentView === 'years') {
      setYearGroupStart((prev) => prev + 12);
    } else if (currentView === 'months') {
      setViewYear((prev) => prev + 1);
    } else {
      if (viewMonth === 12) {
        setViewMonth(1);
        setViewYear((prev) => prev + 1);
      } else {
        setViewMonth((prev) => prev + 1);
      }
    }
  }, [currentView, viewMonth]);

  // Selection handlers
  const handleYearSelect = useCallback(
    (year: number) => {
      if (mode === 'year') {
        onChange({ year });
        handleClose();
      } else {
        setViewYear(year);
        setCurrentView('months');
      }
    },
    [mode, onChange]
  );

  const handleMonthSelect = useCallback(
    (month: number) => {
      if (mode === 'year-month') {
        onChange({ year: viewYear, month });
        handleClose();
      } else {
        setViewMonth(month);
        setCurrentView('days');
      }
    },
    [mode, viewYear, onChange]
  );

  const handleDaySelect = useCallback(
    (day: number) => {
      onChange({ year: viewYear, month: viewMonth, day });
      handleClose();
    },
    [viewYear, viewMonth, onChange]
  );

  const handleTodayClick = useCallback(() => {
    const today = new Date();
    const newValue: DateValue = {
      year: today.getFullYear(),
      month: mode !== 'year' ? today.getMonth() + 1 : undefined,
      day: mode === 'year-month-day' ? today.getDate() : undefined,
    };
    onChange(newValue);
    handleClose();
  }, [mode, onChange]);

  const handleHeaderClick = useCallback(() => {
    if (currentView === 'days') {
      setCurrentView('months');
    } else if (currentView === 'months') {
      // Only allow going to years if mode allows deeper selection
      if (mode !== 'year') {
        setCurrentView('years');
      }
    }
  }, [currentView, mode]);

  // Get header title
  const headerTitle = useMemo(() => {
    if (currentView === 'years') {
      return `${yearGroupStart} — ${yearGroupStart + 11}`;
    }
    if (currentView === 'months') {
      return `${viewYear}`;
    }
    return `${MONTHS[viewMonth]} ${viewYear}`;
  }, [currentView, yearGroupStart, viewYear, viewMonth]);

  // Render years grid
  const renderYearsView = () => {
    const years: number[] = [];
    for (let i = 0; i < 12; i++) {
      years.push(yearGroupStart + i);
    }

    const currentYear = new Date().getFullYear();

    return (
      <Box sx={{ display: 'flex', flexWrap: 'wrap', p: 1 }}>
        {years.map((year) => (
          <YearCell
            key={year}
            onClick={() => handleYearSelect(year)}
            isSelected={value.year === year}
            isCurrent={currentYear === year}
            disabled={year < minYear || year > maxYear}
          >
            {year}
          </YearCell>
        ))}
      </Box>
    );
  };

  // Render months grid
  const renderMonthsView = () => {
    const currentMonth = new Date().getMonth() + 1;
    const currentYear = new Date().getFullYear();

    return (
      <Box sx={{ display: 'flex', flexWrap: 'wrap', p: 1 }}>
        {Object.entries(MONTHS_SHORT).map(([monthNum, monthName]) => {
          const month = Number(monthNum);
          const isDisabled = isMonthDisabled ? isMonthDisabled(viewYear, month) : false;
          return (
            <MonthCell
              key={month}
              onClick={() => !isDisabled && handleMonthSelect(month)}
              isSelected={value.year === viewYear && value.month === month}
              isCurrent={currentYear === viewYear && currentMonth === month}
              disabled={isDisabled}
              sx={isDisabled ? { opacity: 0.4, cursor: 'not-allowed' } : undefined}
            >
              {monthName}
            </MonthCell>
          );
        })}
      </Box>
    );
  };

  // Render days grid
  const renderDaysView = () => {
    const daysInMonth = getDaysInMonth(viewYear, viewMonth);
    const firstDay = getFirstDayOfMonth(viewYear, viewMonth);
    const today = new Date();
    const todayYear = today.getFullYear();
    const todayMonth = today.getMonth() + 1;
    const todayDay = today.getDate();

    // Previous month days
    const prevMonthDays: { day: number; currentMonth: boolean }[] = [];
    if (firstDay > 0) {
      const prevMonth = viewMonth === 1 ? 12 : viewMonth - 1;
      const prevYear = viewMonth === 1 ? viewYear - 1 : viewYear;
      const prevMonthDaysCount = getDaysInMonth(prevYear, prevMonth);
      for (let i = firstDay - 1; i >= 0; i--) {
        prevMonthDays.push({ day: prevMonthDaysCount - i, currentMonth: false });
      }
    }

    // Current month days
    const currentMonthDays: { day: number; currentMonth: boolean }[] = [];
    for (let day = 1; day <= daysInMonth; day++) {
      currentMonthDays.push({ day, currentMonth: true });
    }

    // Next month days
    const nextMonthDays: { day: number; currentMonth: boolean }[] = [];
    const totalCells = prevMonthDays.length + currentMonthDays.length;
    const remainingCells = totalCells % 7 === 0 ? 0 : 7 - (totalCells % 7);
    for (let day = 1; day <= remainingCells; day++) {
      nextMonthDays.push({ day, currentMonth: false });
    }

    const allDays = [...prevMonthDays, ...currentMonthDays, ...nextMonthDays];

    return (
      <Box sx={{ p: 1 }}>
        {/* Weekday headers */}
        <Box sx={{ display: 'flex', mb: 0.5 }}>
          {WEEKDAYS.map((day) => (
            <Box
              key={day}
              sx={{
                flex: 1,
                textAlign: 'center',
                py: 0.5,
                fontSize: '0.75rem',
                color: 'text.secondary',
                fontWeight: 500,
              }}
            >
              {day}
            </Box>
          ))}
        </Box>

        {/* Days grid */}
        <Box sx={{ display: 'flex', flexWrap: 'wrap' }}>
          {allDays.map((item, index) => {
            const isSelected =
              item.currentMonth &&
              value.year === viewYear &&
              value.month === viewMonth &&
              value.day === item.day;
            const isToday =
              item.currentMonth &&
              todayYear === viewYear &&
              todayMonth === viewMonth &&
              todayDay === item.day;

            return (
              <Box key={index} sx={{ width: '14.28%', display: 'flex', justifyContent: 'center' }}>
                <GridCell
                  onClick={() => item.currentMonth && handleDaySelect(item.day)}
                  isSelected={isSelected}
                  isToday={isToday}
                  isCurrentMonth={item.currentMonth}
                  disabled={!item.currentMonth}
                >
                  {item.day}
                </GridCell>
              </Box>
            );
          })}
        </Box>
      </Box>
    );
  };

  return (
    <>
      <TriggerButton
        variant="outlined"
        size={size}
        onClick={handleOpen}
        disabled={disabled}
        startIcon={<CalendarMonth fontSize="small" />}
        sx={{ minWidth: mode === 'year' ? 100 : mode === 'year-month' ? 160 : 150 }}
      >
        {displayValue}
      </TriggerButton>

      <Modal
        open={open}
        onClose={handleClose}
        closeAfterTransition
        slots={{ backdrop: Backdrop }}
        slotProps={{
          backdrop: {
            timeout: 300,
            sx: { backgroundColor: 'rgba(0, 0, 0, 0.5)' },
          },
        }}
      >
        <Fade in={open}>
          <Paper
            sx={{
              position: 'absolute',
              top: '50%',
              left: '50%',
              transform: 'translate(-50%, -50%)',
              width: currentView === 'days' ? 320 : 280,
              maxWidth: '90vw',
              borderRadius: 3,
              boxShadow: 24,
              outline: 'none',
            }}
          >
            {/* Header */}
            <CalendarHeader>
              <IconButton size="small" onClick={navigatePrevious}>
                <ChevronLeft />
              </IconButton>

              <HeaderTitle
                onClick={handleHeaderClick}
                disabled={currentView === 'years' || (currentView === 'months' && mode === 'year')}
              >
                {headerTitle}
              </HeaderTitle>

              <IconButton size="small" onClick={navigateNext}>
                <ChevronRight />
              </IconButton>
            </CalendarHeader>

            {/* Content */}
            {currentView === 'years' && renderYearsView()}
            {currentView === 'months' && renderMonthsView()}
            {currentView === 'days' && renderDaysView()}

            {/* Footer */}
            <Box sx={{ p: 1.5, borderTop: 1, borderColor: 'divider', display: 'flex', gap: 1 }}>
              {showTodayButton && (
                <Button
                  variant="outlined"
                  size="small"
                  onClick={handleTodayClick}
                  sx={{ flex: 1 }}
                >
                  Сегодня
                </Button>
              )}
              <Button
                variant="text"
                size="small"
                onClick={handleClose}
                sx={{ flex: 1 }}
              >
                Закрыть
              </Button>
            </Box>
          </Paper>
        </Fade>
      </Modal>
    </>
  );
}

export default DatePicker;
